<template>
  <div class="fixed inset-0 z-50 overflow-hidden">
    <!-- Backdrop -->
    <Transition name="fade">
      <div 
        v-show="true" 
        class="absolute inset-0 bg-gray-500 bg-opacity-50 fade-in" 
        @click="$emit('close')"
      ></div>
    </Transition>
    
    <!-- Slide-over panel -->
    <Transition name="slide">
      <div 
        v-show="true" 
        class="absolute inset-y-0 right-0 max-w-[800px] w-full bg-white shadow-xl slide-in"
      >
        <div class="h-full flex flex-col">
          <!-- Header -->
          <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900 flex items-center">
              <Plus class="w-5 h-5 mr-2" />
              Create New Path Step
            </h2>
            <button 
              class="p-1 rounded-md text-gray-400 hover:text-gray-500"
              @click="$emit('close')"
            >
              <X class="w-5 h-5" />
            </button>
          </div>
          
          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-4">
            <div class="border-[1px] border-gray-200 rounded-lg p-6 bg-white">
              <form @submit.prevent="handleSubmit" class="space-y-6">
                <div class="space-y-4">
                  <!-- Task Name -->
                  <div class="space-y-2">
                    <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Task Title *
                    </label>
                    <input 
                      v-model="formData.task_data.name" 
                      placeholder="Enter task title" 
                      :disabled="isCreating"
                      required
                      class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                  
                  <!-- Task URL -->
                  <div class="space-y-2">
                    <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Task URL
                    </label>
                    <input 
                      v-model="formData.task_data.url" 
                      placeholder="Enter task URL" 
                      type="url"
                      :disabled="isCreating"
                      class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>

                  <!-- Section -->
                  <div class="space-y-2">
                    <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Section
                    </label>
                    <select
                      v-model="formData.task_data.section"
                      :disabled="isCreating || sectionsStore.isLoading"
                      class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a section</option>
                      <option 
                        v-for="section in sectionsStore.sections" 
                        :key="section.id" 
                        :value="section.id"
                      >
                        {{ section.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Assigned Mentor -->
                  <div class="space-y-2">
                    <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Assigned Mentor
                    </label>
                    <select
                      v-model="formData.task_data.assigned_mentor"
                      :disabled="isCreating || usersStore.guidesAndMentorsLoading"
                      class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a mentor</option>
                      <option v-for="mentor in usersStore.guidesAndMentors" :key="mentor.id" :value="mentor.id">
                        {{ mentor.first_name }} {{ mentor.last_name }} ({{ mentor.username }})
                      </option>
                    </select>
                  </div>

                  <!-- Task Details -->
                  <div class="space-y-2">
                    <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Task Details
                    </label>
                    <textarea 
                      v-model="formData.task_data.details" 
                      placeholder="Enter task details" 
                      :disabled="isCreating"
                      class="bg-lightGray flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    ></textarea>
                  </div>
                  
                  <!-- Numeric fields -->
                  <div class="grid gap-4 grid-cols-1 sm:grid-cols-3">
                    <!-- Points -->
                    <div class="space-y-2">
                      <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Points *
                      </label>
                      <input 
                        v-model.number="formData.points" 
                        type="number" 
                        min="0" 
                        step="0.5" 
                        required
                        :disabled="isCreating"
                        class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                    
                    <!-- Week Order -->
                    <div class="space-y-2">
                      <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Week Order *
                      </label>
                      <input 
                        v-model.number="formData.week_order" 
                        type="number" 
                        min="1" 
                        required
                        :disabled="isCreating"
                        class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                    
                    <!-- Order Within Week -->
                    <div class="space-y-2">
                      <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Order Within Week
                      </label>
                      <input 
                        v-model.number="formData.order_within_week" 
                        type="number" 
                        min="1" 
                        :disabled="isCreating"
                        class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  </div>

                  <hr class="my-4" />
                  <h2 class="text-sm text-gray-500 font-medium uppercase">Task Properties</h2>

                  <div class="space-y-6">
                    <!-- Is Required -->
                    <div>
                      <SwitchToggle
                        v-model="formData.is_required"
                        :disabled="isCreating"
                        label="Required Step"
                        description="This task is a mandatory step in the process"
                      />
                    </div>

                    <!-- Review Required -->
                    <div>
                      <SwitchToggle
                        v-model="formData.task_data.review_required"
                        :disabled="isCreating"
                        label="Review Required"
                        description="This task requires review before completion"
                      />
                    </div>

                    <!-- Fellow Reportable -->
                    <div>
                      <SwitchToggle
                        v-model="formData.task_data.fellow_reportable"
                        :disabled="isCreating"
                        label="Fellow Reportable"
                        description="Fellows can report other fellows for this task"
                      />
                    </div>

                    <!-- Has External Dependency -->
                    <div>
                      <SwitchToggle
                        v-model="formData.has_external_dependency"
                        :disabled="isCreating"
                        label="Has External Dependency"
                        description="This task depends on external factors like scheduling"
                      />
                    </div>
                  </div>

                  <hr class="my-4" />
                </div>
                
                <div class="flex justify-end space-x-2 pt-6">
                  <button 
                    type="button"
                    @click="$emit('close')" 
                    :disabled="isCreating"
                    class="inline-flex text-xs items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    @click="handleSubmit"
                    :disabled="isCreating"
                    class="inline-flex text-xs items-center justify-center rounded-md disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-black text-white hover:bg-black/80 hover:-translate-y-[1px] transition-all duration-200"
                  >
                    <Loader2 v-if="isCreating" class="w-4 h-4 mr-2 animate-spin" />
                    {{ isCreating ? 'Creating...' : 'Create Path Step' }}
                  </button>
                </div>

                <div v-if="error" class="mt-2 text-sm text-red-500">
                  {{ error }}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { usePathStepsStore } from '@/stores/pathSteps'
import { useSectionsStore } from '@/stores/sections'
import { useUsersStore } from '@/stores/users'
import type { CreatePathStepPayload } from '@/stores/pathSteps'
import { Loader2, Plus, X } from 'lucide-vue-next'
import { toast } from '@/components/ui/toast'
import { SwitchToggle } from '@/components/ui/switch'

const pathStepsStore = usePathStepsStore()
const sectionsStore = useSectionsStore()
const usersStore = useUsersStore()

interface Props {
  pathId: number
}

const props = defineProps<Props>()
const emit = defineEmits(['close', 'created'])

const error = ref<string | null>(null)
const isCreating = ref(false)

const formData = reactive({
  task_data: {
    name: '',
    url: '',
    details: '',
    section: null as number | null,
    review_required: true,
    fellow_reportable: false,
    assigned_mentor: null as number | null
  },
  week_order: 1,
  order_within_week: null as number | null,
  is_required: true,
  points: 0,
  has_external_dependency: false
})

onMounted(async () => {
  try {
    await sectionsStore.fetchSections()
    await usersStore.fetchGuidesAndMentors()
  } catch (err) {
    error.value = 'Failed to load data. Please try again.'
  }
})

const handleSubmit = async () => {
  if (!props.pathId) {
    error.value = 'Invalid path ID'
    return
  }

  isCreating.value = true
  error.value = null
  
  try {
    // Create the payload with the supported fields for creation
    const createPayload: CreatePathStepPayload = {
      task_data: {
        name: formData.task_data.name,
        url: formData.task_data.url,
        details: formData.task_data.details,
        section: formData.task_data.section,
        review_required: formData.task_data.review_required,
        fellow_reportable: formData.task_data.fellow_reportable
      },
      week_order: formData.week_order,
      order_within_week: formData.order_within_week,
      is_required: formData.is_required,
      points: formData.points
    }

    console.log('Creating path step with payload:', createPayload)
    const result = await pathStepsStore.createPathStepWithTask(props.pathId, createPayload)
    
    if (result) {
      // If we have additional fields to update, make a separate update call
      const needsUpdate = formData.task_data.assigned_mentor || formData.has_external_dependency
      
      if (needsUpdate) {
        const updatePayload: any = {
          task: {
            id: result.task.id,
            name: formData.task_data.name,
            url: formData.task_data.url || '',
            details: formData.task_data.details || '',
            section: formData.task_data.section,
            review_required: formData.task_data.review_required,
            fellow_reportable: formData.task_data.fellow_reportable,
            assigned_mentor: formData.task_data.assigned_mentor
          },
          has_external_dependency: formData.has_external_dependency
        }
        
        console.log('Updating path step with additional fields:', updatePayload)
        await pathStepsStore.updatePathStep(result.id, updatePayload)
      }

      toast({
        title: 'Path step created successfully',
        description: 'The new step has been added to the curriculum.'
      })
      emit('created')
      emit('close')
    }
  } catch (err) {
    error.value = 'Failed to create path step. Please try again.'
    console.error('Error creating path step:', err)
  } finally {
    isCreating.value = false
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.slide-in {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
</style>